import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Lightbox.css';

// Full-screen viewer for a list of photos and videos. Controlled: the parent
// passes the `items`, the open `index` and the two callbacks; the lightbox owns
// its keyboard, swipe, focus and scroll lock.
//
//   items: [{ type: 'image' | 'video', src, poster, alt, caption }]
//
// Esc closes, the arrow keys and a horizontal swipe step through, focus moves
// to the close button on open and returns to whatever opened it on close, and
// the page behind stops scrolling while it is open. It renders into
// document.body, so no stacking context on the page (the sticky header, a
// transformed section) can draw over it.
export default function Lightbox({ items, index, onIndex, onClose, title }) {
  const closeRef = useRef(null);
  const touchX = useRef(null);
  const count = items.length;
  const item = items[index];

  const step = useCallback((d) => onIndex((index + d + count) % count), [index, count, onIndex]);

  useEffect(() => {
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && count > 1) step(1);
      else if (e.key === 'ArrowLeft' && count > 1) step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, step, onClose]);

  if (!item) return null;

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null || count < 2) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
  };

  return createPortal(
    <div
      className="lb"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Photo viewer'}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button type="button" className="lb-close" onClick={onClose} ref={closeRef} aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>

      <figure className="lb-figure">
        {item.type === 'video' ? (
          <video
            key={item.src}
            className="lb-media"
            src={item.src}
            poster={item.poster || undefined}
            controls
            autoPlay
            muted
            loop
            playsInline
            aria-label={item.alt}
          />
        ) : (
          <img key={item.src} className="lb-media" src={item.src} alt={item.alt} />
        )}
        {(item.caption || count > 1) && (
          <figcaption className="lb-caption">
            {item.caption && <span>{item.caption}</span>}
            {count > 1 && <span className="lb-count">{index + 1} / {count}</span>}
          </figcaption>
        )}
      </figure>

      {count > 1 && (
        <>
          <button type="button" className="lb-nav lb-nav--prev" onClick={() => step(-1)} aria-label="Previous">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button type="button" className="lb-nav lb-nav--next" onClick={() => step(1)} aria-label="Next">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
